import { Loader, LinkButton, ErrorMessage, ImageRadioGroup } from '@communityshare/ui-shared';
import { useConnect } from '@lore/query-connect';
import { PayloadStates } from '@lore/utils';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { useQueryMutation } from '@lore/query-connect';
import useEventImage from '@/hooks/useEventImage';
import useEventChannel from '@/hooks/useEventChannel';
import formatEventDate from '@/features/share-event/utils/formatEventDate';
import usePreviewImages from '@/features/share-project/hooks/usePreviewImages';
import fallbackImage from '@assets/boy_magnifying_glass_butterfly.jpg';

// Window name for the preview tab: repeat clicks reuse the tab instead of
// opening new ones, and gives the BroadcastChannel peer a known identity.
const PREVIEW_WINDOW_NAME = 'cs-share-event-preview';

EventLinkPreview.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    state: PropTypes.string,
    data: PropTypes.shape({
      activityId: PropTypes.number,
      siteId: PropTypes.number,
      title: PropTypes.string,
      shared: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string])
    })
  }).isRequired
};

export default function EventLinkPreview(props) {
  const { event } = props;
  const { t } = useTranslation();
  // Default to tag 1 so byId never throws for events without an activity
  const activity = useConnect('tag.byId', {
    id: event?.data?.activityId ?? 1
  });
  const siteId = event?.data?.siteId ?? null;
  // Pass 0 when siteId is absent so the blueprint doesn't throw 'Missing id';
  // a 0-id query returns NOT_FOUND harmlessly.
  const site = useConnect('site.byId', {
    id: siteId ?? 0
  });
  const [hostname, setHostname] = useState('...');
  // Local selection; null defers to the saved image (or the default)
  const [selectedImageId, setSelectedImageId] = useState(null);
  const eventImage = useEventImage(event.id);
  const { loading: imagesLoading, images } = usePreviewImages(event.data.activityId);
  const broadcast = useEventChannel(event.id);
  const defaultSavedRef = useRef(false);
  const markSharedRef = useRef(false);
  const sharedMutation = useQueryMutation('event.update', event);

  // Opening the share dialog is currently the only signal that an event
  // has been shared, so flip the flag once if it isn't already set.
  useEffect(() => {
    const isReady = [PayloadStates.RESOLVED, PayloadStates.UPDATING].includes(event.state) && event.data;
    const alreadyShared = event.data?.shared === true || event.data?.shared === 1 || event.data?.shared === '1';
    if (isReady && !alreadyShared && !markSharedRef.current) {
      markSharedRef.current = true;
      sharedMutation.mutateAsync({ shared: true }).catch((error) => {
        // Allow a retry on the next render if the save failed.
        markSharedRef.current = false;
        console.error('Failed to mark event as shared', error);
      });
    }
  }, [event.state, event.data]);

  useEffect(() => {
    setHostname(window.location.hostname);
  }, []);

  // An event with no saved image gets the default persisted once,
  // so the share page renders the same photo the preview shows.
  useEffect(() => {
    if (eventImage.isSuccess && !eventImage.image && !imagesLoading && !defaultSavedRef.current) {
      defaultSavedRef.current = true;
      const image = images[0];
      eventImage.upsert({ imageUrl: image.src, width: image.width, height: image.height });
    }
  }, [eventImage.isSuccess, eventImage.image, imagesLoading]);

  if ([PayloadStates.FETCHING, PayloadStates.INITIAL_STATE].includes(event.state) || eventImage.isLoading || imagesLoading) {
    return (
      <div className="flex flex-col justify-center h-[80vh]">
        <Loader/>
      </div>
    )
  }

  if (![PayloadStates.RESOLVED, PayloadStates.UPDATING].includes(event.state) || !event.data) {
    return (
      <div className="flex flex-col items-center">
        <ErrorMessage v2={true}>
          {t('events.share.data_error')}
        </ErrorMessage>
      </div>
    )
  }

  // Local selection wins (optimistic), then the saved image;
  // null renders the PhotoIcon empty state until an image exists
  const savedImage = images.find(image => image.src === eventImage.image?.imageUrl);
  const shareImage = images.find(image => image.id === selectedImageId) ?? savedImage ?? null;

  const dateLabel = formatEventDate(event.data);

  // The preview swaps immediately while the save runs;
  // a failure surfaces through eventImage.upsertError below.
  function onShareImageChange(imageId) {
    setSelectedImageId(imageId);
    const image = images.find(image => image.id === imageId);
    eventImage.upsert({
      imageUrl: image.src,
      width: image.width,
      height: image.height
    });
    broadcast({ imageUrl: image.src });
  }

  function onPreview() {
    window.open(
      `${window.location.origin}/share/event/${event.id}`,
      `${PREVIEW_WINDOW_NAME}-${event.id}`
    );
  }

  return (
    <div className="bg-white border border-cs-gray-200 rounded-cs-10 p-5">
      <h3 className="text-xl font-semibold text-cs-gray-900 mb-4">
        {t('events.share.link_preview')}
      </h3>
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* overflow-hidden clips the image to the card's rounded corners */}
        <div className="flex-1 min-w-0 bg-white border border-cs-gray-200 rounded-cs-10 overflow-hidden">
          {shareImage ? (
            // key remounts the img on swap so the fade-in animation replays
            <img
              id={`share-image-${shareImage.id}`}
              key={shareImage.id}
              className="w-full aspect-[16/9] object-cover animate-fade-in"
              src={shareImage.src}
              alt={t('events.share.preview_image_alt')}
              // Curated WordPress URLs can 404; fall back to a bundled image.
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = fallbackImage;
              }}
            />
          ) : (
            <div className="w-full aspect-[16/9] flex items-center justify-center bg-cs-gray-100 animate-fade-in">
              <PhotoIcon className="size-16 text-cs-gray-400" aria-hidden="true" />
            </div>
          )}
          <div className="px-5">
            <p className="mt-3 text-sm text-cs-gray-500">
              {hostname}/share/event/{event.id}
            </p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-cs-orange-500">
              {activity.state === PayloadStates.FETCHING && <Loader/>}
              {activity?.data?.name ?? '...'}{siteId && site.state !== PayloadStates.FETCHING ? ` | ${site?.data?.city ?? ''}, ${site?.data?.state ?? ''}` : ''}
            </p>
            <p className="mt-1 text-base font-semibold text-cs-gray-900">
              {event.data.title}
            </p>
            {dateLabel && (
              <p className="mt-1 text-sm text-cs-gray-500">
                {dateLabel}
              </p>
            )}
            <div className="mt-2 pt-2 border-t border-cs-gray-200 flex justify-end">
              <LinkButton label={t('events.share.preview_page')} icon="right" onClick={onPreview}/>
            </div>
          </div>
        </div>
        <ImageRadioGroup
          label={t('events.share.swap_photo')}
          images={images}
          value={shareImage?.id ?? null}
          onChange={onShareImageChange}
          getImageLabel={(image, index) => t('events.share.photo_option', { number: index + 1 })}
          className="lg:w-36 lg:flex-shrink-0"
          optionsClassName="flex gap-2 lg:flex-col"
          optionClassName="flex-1 min-w-0 lg:flex-none"
        />
      </div>
      {eventImage.upsertError && (
        <div className="mt-4">
          <ErrorMessage v2={true}>
            {t('events.share.image_error')}
          </ErrorMessage>
        </div>
      )}
    </div>
  );
}
